import {
  discoveryFromRoomCandidate,
  normalizeHostId,
  normalizeLanIpv4,
  type HostDiscovery,
} from "./discovery.js";

export const ROOM_PROBE_TIMEOUT_MS = 3_000;
export const ROOM_QUERY_PATH = "/v1/rooms/query";
export const ROOM_REVEAL_PATH = "/v1/rooms/reveal";

const MAX_QUERY_PAGES = 5;
const MAX_QUERY_BYTES = 128 * 1024;
const MAX_REVEAL_BYTES = 16 * 1024;
const MAX_CANDIDATES = 100;
const MAX_CANDIDATE_IPS = 8;
const MAX_CURSOR_LENGTH = 512;
const MAX_LABEL_CODE_POINTS = 64;
const PROOF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/u;
const MATCH_TOKEN_PATTERN = /^[0-9A-HJKMNP-TV-Z]{4}$/u;

export interface OpaqueRoomCandidate {
  readonly hostId: string;
  /**
   * Registry egress-prefilter metadata only. The browser connects exclusively
   * through the canonical c.<hostId> multi-A name and never selects a socket
   * endpoint from this list.
   */
  readonly candidateIPs: readonly string[];
}

export type RoomQueryResult =
  | {
      readonly kind: "rooms";
      readonly candidates: readonly OpaqueRoomCandidate[];
    }
  | { readonly kind: "many" };

export class RoomDirectoryThrottledError extends Error {
  public constructor() {
    super("room directory query throttled");
    this.name = "RoomDirectoryThrottledError";
  }
}

export interface ReachabilityProof {
  readonly hostId: string;
  readonly proofToken: string;
  readonly matchToken: string;
}

export interface RevealedRoom {
  readonly hostId: string;
  readonly label: string;
  readonly matchToken: string;
}

export type RoomProbeFailureKind =
  | "blocked"
  | "guest-vlan"
  | "registered-unreachable";

export class RoomProbeError extends Error {
  public constructor(
    public readonly kind: RoomProbeFailureKind,
    message: string,
  ) {
    super(message);
    this.name = "RoomProbeError";
  }
}

export class RoomRevealCorrelationError extends Error {
  public constructor() {
    super("room reveal match-token mismatch");
    this.name = "RoomRevealCorrelationError";
  }
}

export interface RoomProbe {
  probe(candidate: OpaqueRoomCandidate): Promise<ReachabilityProof>;
}

export interface RoomDiscoveryHttp {
  readonly fetch: typeof fetch;
  readonly setTimeout?: typeof globalThis.setTimeout;
  readonly clearTimeout?: typeof globalThis.clearTimeout;
}

interface ProbeSocket {
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  onerror: ((event: Event) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  close(code?: number, reason?: string): void;
}

export interface BrowserRoomProbeOptions {
  readonly createSocket?: (url: string) => ProbeSocket;
  readonly setTimeout?: typeof globalThis.setTimeout;
  readonly clearTimeout?: typeof globalThis.clearTimeout;
  readonly timeoutMs?: number;
}

export function discoveryApiBaseUrl(
  configuredValue: string | undefined,
): string | undefined {
  if (configuredValue === undefined || configuredValue.trim().length === 0)
    return undefined;
  let parsed: URL;
  try {
    parsed = new URL(configuredValue.trim());
  } catch {
    return undefined;
  }
  if (
    parsed.protocol !== "https:" ||
    parsed.username.length > 0 ||
    parsed.password.length > 0 ||
    parsed.search.length > 0 ||
    parsed.hash.length > 0
  ) {
    return undefined;
  }
  return parsed.href.replace(/\/$/u, "");
}

export async function queryRoomDirectory(
  apiBaseUrl: string,
  http: RoomDiscoveryHttp = { fetch: globalThis.fetch },
): Promise<RoomQueryResult> {
  const candidates: OpaqueRoomCandidate[] = [];
  const byHost = new Map<string, OpaqueRoomCandidate>();
  let cursor: string | null = null;

  for (let page = 0; page < MAX_QUERY_PAGES; page += 1) {
    const response = await http.fetch(
      endpointUrl(apiBaseUrl, ROOM_QUERY_PATH),
      {
        method: "POST",
        mode: "cors",
        cache: "no-store",
        credentials: "omit",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(cursor === null ? {} : { cursor }),
      },
    );
    if (response.status === 429) throw new RoomDirectoryThrottledError();
    if (!response.ok) throw new Error("room directory query failed");
    const payload = parseJsonObject(
      await boundedResponseText(response, MAX_QUERY_BYTES),
      "room directory query",
    );
    const pageResult = parseQueryPage(payload);
    if (pageResult.status === "many") return { kind: "many" };

    for (const candidate of pageResult.candidates) {
      const prior = byHost.get(candidate.hostId);
      if (prior === undefined) {
        byHost.set(candidate.hostId, candidate);
        candidates.push(candidate);
      } else {
        const merged = mergeCandidateIps(prior, candidate);
        byHost.set(candidate.hostId, merged);
        const index = candidates.findIndex(
          (current) => current.hostId === candidate.hostId,
        );
        if (index >= 0) candidates[index] = merged;
      }
      if (candidates.length > MAX_CANDIDATES) return { kind: "many" };
    }

    cursor = pageResult.nextCursor;
    if (cursor === null) return { kind: "rooms", candidates };
  }

  // A server that keeps paginating beyond the bounded client budget is safer
  // to present as a CGNAT-sized bucket than as a partial, apparently complete list.
  return { kind: "many" };
}

export async function revealRoom(
  apiBaseUrl: string,
  proof: ReachabilityProof,
  http: RoomDiscoveryHttp = { fetch: globalThis.fetch },
): Promise<RevealedRoom> {
  const hostId = normalizeHostId(proof.hostId);
  const proofToken = patternString(
    proof.proofToken,
    PROOF_TOKEN_PATTERN,
    "proofToken",
  );
  const expectedMatchToken = patternString(
    proof.matchToken,
    MATCH_TOKEN_PATTERN,
    "matchToken",
  );
  return withDiscoveryAbortDeadline(http, async (signal) => {
    const response = await http.fetch(
      endpointUrl(apiBaseUrl, ROOM_REVEAL_PATH),
      {
        method: "POST",
        mode: "cors",
        cache: "no-store",
        credentials: "omit",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ hostId, proofToken }),
        signal,
      },
    );
    if (!response.ok) throw new Error("room reveal failed");
    const payload = parseJsonObject(
      await boundedResponseText(response, MAX_REVEAL_BYTES),
      "room reveal",
    );
    assertExactKeys(payload, ["hostId", "label", "matchToken"]);
    const revealedHostId = normalizeHostId(requiredString(payload, "hostId"));
    if (revealedHostId !== hostId) throw new Error("room reveal host mismatch");
    const matchToken = patternString(
      requiredString(payload, "matchToken"),
      MATCH_TOKEN_PATTERN,
      "matchToken",
    );
    if (matchToken !== expectedMatchToken)
      throw new RoomRevealCorrelationError();
    const label = requiredString(payload, "label").normalize("NFC").trim();
    if (
      Array.from(label).length < 1 ||
      Array.from(label).length > MAX_LABEL_CODE_POINTS ||
      /[\p{Cc}\p{Cf}\p{Cs}]/u.test(label)
    )
      throw new Error("room reveal label is invalid");
    return { hostId, label, matchToken };
  });
}

export function createBrowserRoomProbe(
  options: BrowserRoomProbeOptions = {},
): RoomProbe {
  const createSocket =
    options.createSocket ?? ((url: string) => new WebSocket(url));
  const schedule = options.setTimeout ?? globalThis.setTimeout;
  const cancel = options.clearTimeout ?? globalThis.clearTimeout;
  const timeoutMs = options.timeoutMs ?? ROOM_PROBE_TIMEOUT_MS;
  if (
    !Number.isInteger(timeoutMs) ||
    timeoutMs <= 0 ||
    timeoutMs > ROOM_PROBE_TIMEOUT_MS
  ) {
    throw new Error(
      "room probe timeout must be within the three-second budget",
    );
  }

  return {
    probe(candidate) {
      const discovery = discoveryForCandidate(candidate);
      const url = new URL(discovery.signalUrl);
      url.pathname = "/probe";
      url.search = "";
      url.hash = "";

      return new Promise<ReachabilityProof>((resolve, reject) => {
        let socket: ProbeSocket;
        let opened = false;
        let settled = false;
        const finish = (
          result:
            | { readonly ok: true; readonly proof: ReachabilityProof }
            | { readonly ok: false; readonly error: RoomProbeError },
        ) => {
          if (settled) return;
          settled = true;
          cancel(timeout);
          socket.onopen = null;
          socket.onmessage = null;
          socket.onerror = null;
          socket.onclose = null;
          try {
            socket.close(1000, "reachability probe complete");
          } catch {
            // The outcome is already fixed; close is best effort only.
          }
          if (result.ok) resolve(result.proof);
          else reject(result.error);
        };
        const timeout = schedule(() => {
          finish({
            ok: false,
            error: new RoomProbeError(
              opened ? "registered-unreachable" : "guest-vlan",
              "room probe timed out before proof",
            ),
          });
        }, timeoutMs);

        try {
          socket = createSocket(url.href);
        } catch (error) {
          cancel(timeout);
          const blocked =
            error instanceof DOMException &&
            (error.name === "SecurityError" ||
              error.name === "NotAllowedError");
          reject(
            new RoomProbeError(
              blocked ? "blocked" : "registered-unreachable",
              "room probe could not start",
            ),
          );
          return;
        }

        socket.onopen = () => {
          opened = true;
        };
        socket.onmessage = (event) => {
          if (!opened || typeof event.data !== "string") {
            finish({
              ok: false,
              error: new RoomProbeError(
                "registered-unreachable",
                "room probe returned an invalid proof frame",
              ),
            });
            return;
          }
          try {
            const proof = parseProofFrame(event.data, discovery.hostId);
            finish({ ok: true, proof });
          } catch {
            finish({
              ok: false,
              error: new RoomProbeError(
                "registered-unreachable",
                "room probe returned an invalid proof frame",
              ),
            });
          }
        };
        socket.onerror = () => {
          finish({
            ok: false,
            error: new RoomProbeError(
              "registered-unreachable",
              "room probe WebSocket failed",
            ),
          });
        };
        socket.onclose = () => {
          finish({
            ok: false,
            error: new RoomProbeError(
              "registered-unreachable",
              "room probe closed before proof",
            ),
          });
        };
      });
    },
  };
}

export function discoveryForCandidate(
  candidate: OpaqueRoomCandidate,
): HostDiscovery {
  return discoveryFromRoomCandidate(candidate.hostId);
}

function parseProofFrame(
  source: string,
  expectedHostId: string,
): ReachabilityProof {
  if (source.length > MAX_REVEAL_BYTES)
    throw new Error("room proof frame is too large");
  const payload = parseJsonObject(source, "room probe");
  assertExactKeys(payload, ["type", "hostId", "proofToken", "matchToken"]);
  if (payload.type !== "reachability-proof")
    throw new Error("room proof frame has an invalid type");
  const hostId = normalizeHostId(requiredString(payload, "hostId"));
  if (hostId !== expectedHostId) throw new Error("room proof host mismatch");
  return {
    hostId,
    proofToken: patternString(
      requiredString(payload, "proofToken"),
      PROOF_TOKEN_PATTERN,
      "proofToken",
    ),
    matchToken: patternString(
      requiredString(payload, "matchToken"),
      MATCH_TOKEN_PATTERN,
      "matchToken",
    ),
  };
}

function parseQueryPage(payload: Record<string, unknown>): {
  readonly status: "ok" | "many";
  readonly candidates: readonly OpaqueRoomCandidate[];
  readonly nextCursor: string | null;
} {
  assertExactKeys(payload, ["status", "candidates", "nextCursor"]);
  if (payload.status !== "ok" && payload.status !== "many")
    throw new Error("room query status is invalid");
  if (!Array.isArray(payload.candidates))
    throw new Error("room query candidates are invalid");
  const nextCursor = payload.nextCursor;
  if (
    nextCursor !== null &&
    (typeof nextCursor !== "string" ||
      nextCursor.length < 1 ||
      nextCursor.length > MAX_CURSOR_LENGTH)
  ) {
    throw new Error("room query cursor is invalid");
  }
  if (payload.status === "many") {
    if (payload.candidates.length !== 0 || nextCursor !== null)
      throw new Error("many-room response must suppress candidates");
    return { status: "many", candidates: [], nextCursor: null };
  }
  return {
    status: "ok",
    candidates: payload.candidates.map(parseCandidate),
    nextCursor,
  };
}

function parseCandidate(input: unknown): OpaqueRoomCandidate {
  if (!isRecord(input)) throw new Error("room candidate is invalid");
  assertExactKeys(input, ["hostId", "candidateIPs"]);
  const hostId = normalizeHostId(requiredString(input, "hostId"));
  if (
    !Array.isArray(input.candidateIPs) ||
    input.candidateIPs.length < 1 ||
    input.candidateIPs.length > MAX_CANDIDATE_IPS
  ) {
    throw new Error("room candidate addresses are invalid");
  }
  const candidateIPs = input.candidateIPs.map((value) => {
    if (typeof value !== "string")
      throw new Error("room candidate address is invalid");
    return normalizeLanIpv4(value);
  });
  return { hostId, candidateIPs: [...new Set(candidateIPs)] };
}

function mergeCandidateIps(
  left: OpaqueRoomCandidate,
  right: OpaqueRoomCandidate,
): OpaqueRoomCandidate {
  const candidateIPs = [
    ...new Set([...left.candidateIPs, ...right.candidateIPs]),
  ];
  if (candidateIPs.length > MAX_CANDIDATE_IPS)
    throw new Error("room candidate has too many addresses");
  return {
    hostId: left.hostId,
    candidateIPs,
  };
}

function endpointUrl(baseUrl: string, path: string): string {
  const base = discoveryApiBaseUrl(baseUrl);
  if (base === undefined) throw new Error("room discovery API URL is invalid");
  return new URL(`${base}${path}`).href;
}

async function withDiscoveryAbortDeadline<T>(
  http: RoomDiscoveryHttp,
  operation: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const schedule = http.setTimeout ?? globalThis.setTimeout;
  const cancel = http.clearTimeout ?? globalThis.clearTimeout;
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = schedule(() => {
      controller.abort();
      reject(new Error("room reveal timed out"));
    }, ROOM_PROBE_TIMEOUT_MS);
  });

  try {
    return await Promise.race([operation(controller.signal), timeout]);
  } finally {
    if (timer !== undefined) cancel(timer);
  }
}

async function boundedResponseText(
  response: Response,
  maximumBytes: number,
): Promise<string> {
  const declaredLength = response.headers.get("content-length");
  if (
    declaredLength !== null &&
    Number.isFinite(Number(declaredLength)) &&
    Number(declaredLength) > maximumBytes
  ) {
    throw new Error("room discovery response is too large");
  }
  const source = await response.text();
  if (new TextEncoder().encode(source).byteLength > maximumBytes)
    throw new Error("room discovery response is too large");
  return source;
}

function parseJsonObject(
  source: string,
  context: string,
): Record<string, unknown> {
  let value: unknown;
  try {
    value = JSON.parse(source);
  } catch {
    throw new Error(`${context} response is not JSON`);
  }
  if (!isRecord(value)) throw new Error(`${context} response is invalid`);
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function assertExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): void {
  const actual = Object.keys(value).sort();
  const sortedExpected = [...expected].sort();
  if (
    actual.length !== sortedExpected.length ||
    actual.some((key, index) => key !== sortedExpected[index])
  ) {
    throw new Error("room discovery payload fields are invalid");
  }
}

function requiredString(value: Record<string, unknown>, key: string): string {
  const field = value[key];
  if (typeof field !== "string")
    throw new Error(`room discovery ${key} is invalid`);
  return field;
}

function patternString(value: string, pattern: RegExp, name: string): string {
  if (!pattern.test(value))
    throw new Error(`room discovery ${name} is invalid`);
  return value;
}
